/**
 * What the calendar should contain. Pure, and deliberately free of any import
 * that needs build-time configuration, so the rules can be tested directly.
 *
 * The rules, from the brief:
 *
 *   task saved with a deadline, not Done  ->  event created, id written to K
 *   deadline changed                      ->  event moved
 *   task reaches Done                     ->  event deleted, K cleared
 *   task moved back out of Done           ->  event recreated
 *   task soft-deleted                     ->  event deleted, K cleared
 *   no deadline                           ->  no event
 */
import type { Task } from "../types";

/** Google's colour id for Tomato. */
export const TOMATO = "11";

export interface CalendarEvent {
  id: string;
  summary: string;
  /** Start date of an all-day event, "YYYY-MM-DD". */
  date: string;
}

export interface Desired {
  summary: string;
  date: string;
}

/** All-day events end on the following day; Google treats end as exclusive. */
export function dayAfter(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/** The exact event body sent to Google. All-day, Tomato, reminders off. */
export function eventBody(summary: string, date: string) {
  return {
    summary,
    start: { date },
    end: { date: dayAfter(date) },
    colorId: TOMATO,
    reminders: { useDefault: false, overrides: [] as never[] },
  };
}

/**
 * What the calendar should hold for a task, or null for no event at all.
 *
 * Every event is the same colour, so the title has to carry the project.
 */
export function desiredEvent(task: Task, projectName: string): Desired | null {
  if (task.deleted) return null;
  if (task.status === "Done") return null;
  if (!task.deadline) return null;
  const prefix = projectName ? `${projectName} · ` : "";
  return { summary: `${prefix}${task.title}`, date: task.deadline };
}

export type Action =
  | { kind: "create"; task: Task; desired: Desired }
  | { kind: "update"; task: Task; eventId: string; desired: Desired }
  | { kind: "delete"; task: Task; eventId: string };

/**
 * Compare every task against the events actually on the calendar.
 *
 * A task whose event id points at nothing gets its event recreated, which is
 * what repairs an event deleted by hand in Google Calendar. A task whose event
 * has drifted in date or title gets it corrected, which is what reverts a
 * dragged event.
 */
export function planReconcile(
  tasks: Task[],
  projectName: (task: Task) => string,
  events: CalendarEvent[]
): Action[] {
  const byId = new Map(events.map((e) => [e.id, e]));
  const actions: Action[] = [];

  for (const task of tasks) {
    const want = desiredEvent(task, projectName(task));
    const have = task.calendarEventId ? byId.get(task.calendarEventId) : undefined;

    if (want && !have) {
      actions.push({ kind: "create", task, desired: want });
    } else if (want && have) {
      if (have.date !== want.date || have.summary !== want.summary) {
        actions.push({ kind: "update", task, eventId: task.calendarEventId, desired: want });
      }
    } else if (!want && task.calendarEventId) {
      actions.push({ kind: "delete", task, eventId: task.calendarEventId });
    }
  }
  return actions;
}
